import { useTranslation } from "react-i18next";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import { useEffect, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import SortOrder from "components/SortOrder";
import {
  EOrder,
  ETypeOfOrder,
  IOrderObject,
} from "components/SortOrder/typings";
import Loading from "components/Loading";
import Search from "assets/icons/search.gif";
import {
  DataGridPremium,
  GridColumnGroupingModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { IUserInfo } from "dto/Admin/userInfo";
import moment from "moment";
import ExtensionsCell from "./Cells/ExtensionsCell";
import LabelCell from "./Cells/LabelCell";
import { SignalRContext } from "../../../App";
import { HubCallbacksNames } from "api/hub";

const UserRegister: FC<Props> = ({ searchText }) => {
  const { t } = useTranslation();

  const [users, setUsers] = useState<IUserInfo[]>([]);

  const [listFilter, setListFilter] = useState<IOrderObject>({
    type: ETypeOfOrder.LAST_SEEN,
    order: EOrder.DESCENDING,
  });

  const [nextPage, setNextPage] = useState(0);

  SignalRContext.useSignalREffect(
    HubCallbacksNames.Online,
    (message) => {
      const oldList = [...users];

      const toUpdate = oldList.find((x) => x.Id === message.userId);

      if (toUpdate) {
        toUpdate.IsOnline = true;
        setUsers(oldList);
      }
    },
    [],
  );

  SignalRContext.useSignalREffect(
    HubCallbacksNames.Offline,
    (message) => {
      const oldList = [...users];

      const toUpdate = oldList.find((x) => x.Id === message.userId);

      if (toUpdate) {
        toUpdate.IsOnline = false;
        setUsers(oldList);
      }
    },
    [],
  );

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: "Internal",
      description: "",
      headerName: "",
      children: [
        {
          field: "Label",
        },
        {
          field: "CreationDate",
        },
        {
          field: "LastSeen",
        },
      ],
    },

    {
      groupId: "subscription",
      headerName: t("admin.groups.subscription"),
      children: [
        { field: "Subscription.Name", headerName: t("admin.columns.name") },
        {
          field: "Subscription.Extensions",
          headerName: t("admin.columns.extensions"),
        },
      ],
    },

    {
      groupId: "cases",
      headerName: t("admin.groups.cases"),
      children: [
        { field: "Cases.Count", headerName: t("admin.columns.count") },
      ],
    },

    {
      groupId: "contactForm",
      headerName: t("admin.groups.contactForm"),
      children: [
        {
          field: "ContactForm.PrivateForms",
          headerName: t("admin.columns.contactFormPrivate"),
        },
        {
          field: "ContactForm.TeamForms",
          headerName: t("admin.columns.contactFormTeam"),
        },
        {
          field: "ContactForm.AddressBooksLegalPerson",
          headerName: t("admin.columns.addressBookLegal"),
        },
        {
          field: "ContactForm.AddressBooksNaturalPerson",
          headerName: t("admin.columns.addressBookNatural"),
        },
        {
          field: "ContactForm.IncomingMessages",
          headerName: t("admin.columns.contactMessagesIn"),
        },
        {
          field: "ContactForm.OutgoingMessages",
          headerName: t("admin.columns.contactMessagesOut"),
        },
      ],
    },
    {
      groupId: "Email",
      headerName: t("admin.groups.email"),
      description: "",
      children: [
        { field: "Emails.Count", headerName: t("admin.columns.count") },
        { field: "Emails.LastSync", headerName: t("admin.columns.sync") },
      ],
    },

    {
      groupId: "nota",
      headerName: t("admin.groups.nota"),
      children: [
        { field: "Nota.Sent", headerName: t("admin.columns.sent") },
        { field: "Nota.Received", headerName: t("admin.columns.received") },
      ],
    },

    {
      groupId: "CourtPortal",
      headerName: t("admin.groups.courtPortal"),
      description: "",
      children: [
        {
          field: "CourtPortals.DocumentsCount",
          headerName: t("admin.columns.documents"),
        },
        {
          field: "CourtPortals.LawsuitsCount",
          headerName: t("admin.columns.lawsuits"),
        },
        {
          field: "CourtPortals.PortalsCount",
          headerName: t("admin.columns.portals"),
        },
        { field: "CourtPortals.LastSync", headerName: t("admin.columns.sync") },
      ],
    },

    {
      groupId: "dossier",
      headerName: t("admin.groups.dossier"),
      children: [
        { field: "Dossier.Count", headerName: t("admin.columns.count") },
      ],
    },
  ];

  const { data, refetch, fetchNextPage, isLoading, status } = useInfiniteQuery(
    ["users", listFilter, searchText],
    async () => {
      const query = new URLSearchParams(window.location.search);

      if (searchText.length) {
        query.append("Text", searchText);
      }

      // if (userId) {
      //   query.append("PageForId", userId);
      // }

      query.append("Page", nextPage.toString());
      query.append("SortProp", listFilter.type);
      query.append("SortOrder", listFilter.order);

      const res = await axiosInstance.get(`/admin/users?${query}`);
      // return res.data;
      return {
        items: res.data,
        LastPage: res.data.LastPage,
        nextPage: nextPage + 1,
      };
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: false,
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return nextPage + 1;
        }
        return undefined;
      },
    },
  );

  const compileData = async () => {
    if (!data) return;
    setNextPage(data.pages[data.pages.length - 1].nextPage);
    const users = await Promise.all(
      data?.pages
        .map((page) => {
          return page.items.Items;
        })
        .flat(),

      // .sort((left: IPackage, right: IPackage) => {
      //   //@ts-ignore
      //   return right.Unread - left.Unread;
      // })
    );

    setUsers(users);
  };

  useEffect(() => {
    compileData();
  }, [data]);

  useEffect(() => {
    if (!listFilter.initalized) return;

    setNextPage(0);
    setUsers([]);
    setTimeout(() => {
      refetch();
    }, 100);
  }, [listFilter]);
  return (
    <div className={styles.wrapper}>
      <div className={styles.order}>
        <SortOrder
          isLoading={false}
          refetch={() => {
            // setNextPage(0);
            // setClients([]);
            // setTimeout(() => {
            //   refetch();
            // }, 100);
          }}
          setListFilter={setListFilter}
          type={listFilter.type}
          order={listFilter.order}
        />
      </div>

      <div className={styles.list}>
        {!users.length && !isLoading && status !== "idle" && (
          <div className={styles.wrapper}>
            <div className={styles.empty}>
              <img src={Search} />
              <div>{t("emptyStates.noResults")}</div>
            </div>
          </div>
        )}

        {isLoading && <Loading />}
        {!isLoading && (
          <DataGridPremium
            localeText={{
              columnMenuSortAsc: t("grid.sortAsc"),
              columnMenuSortDesc: t("grid.sortDesc"),
              pinToLeft: t("grid.pinToLeft"),
              pinToRight: t("grid.pinToRight"),
              columnMenuFilter: t("grid.filter"),
              columnMenuHideColumn: t("grid.hideColumn"),
              columnMenuShowColumns: t("grid.showColumns"),
              columnMenuManageColumns: t("grid.manageColumns"),
              columnMenuUnsort: t("grid.unsort"),
              columnsManagementNoColumns: t("grid.noColumns"),
            }}
            columnHeaderHeight={30}
            onRowsScrollEnd={() => {
              fetchNextPage();
            }}
            onRowClick={() => {
              // navigate(`/admin/users/${user.Id}`);
            }}
            columns={[
              {
                field: "Label",
                width: 180,
                headerName: t("admin.columns.name"),
                cellClassName: `${styles.nameCell}`,
                renderCell: (
                  params: GridRenderCellParams<IUserInfo, string>,
                ) => <LabelCell element={params.row}></LabelCell>,
              },
              {
                field: "CreationDate",
                width: 140,
                headerName: t("admin.columns.registrationDate"),
                valueFormatter: (v) => {
                  if (v) {
                    return moment(v).format("DD.MM.yyyy HH:mm");
                  }
                  return null;
                },
              },
              {
                field: "LastSeen",
                width: 140,
                headerName: t("admin.columns.lastActivity"),
                valueFormatter: (v) => {
                  if (v) {
                    return moment(v).format("DD.MM.yyyy HH:mm");
                  }
                  return t("common.none");
                },
              },

              {
                field: "Subscription.Name",
                width: 120,
                headerName: t("admin.groups.subscription"),
                valueGetter: (value, row) => row?.Subscription?.Name,
              },
              {
                field: "Subscription.Extensions",
                width: 100,
                headerName: t("admin.columns.extensions"),
                valueGetter: (value, row) => row?.Subscription?.From,
                renderCell: (
                  params: GridRenderCellParams<IUserInfo, string>,
                ) => <ExtensionsCell element={params.row}></ExtensionsCell>,
              },

              {
                field: "Cases.Count",
                width: 120,
                headerName: t("admin.columns.count"),
                valueGetter: (value, row) => row?.Cases?.Count,
              },

              {
                field: "ContactForm.PrivateForms",
                width: 90,
                headerName: t("admin.columns.contactFormPrivate"),
                valueGetter: (value, row) => row?.ContactForm?.PrivateForms,
              },
              {
                field: "ContactForm.TeamForms",
                width: 90,
                headerName: t("admin.columns.contactFormTeam"),
                valueGetter: (value, row) => row?.ContactForm?.TeamForms,
              },
              {
                field: "ContactForm.AddressBooksLegalPerson",
                width: 90,
                headerName: t("admin.columns.addressBookLegal"),
                valueGetter: (value, row) =>
                  row?.ContactForm?.AddressBooksLegalPerson,
              },
              {
                field: "ContactForm.AddressBooksNaturalPerson",
                width: 90,
                headerName: t("admin.columns.addressBookNatural"),
                valueGetter: (value, row) =>
                  row?.ContactForm?.AddressBooksNaturalPerson,
              },
              {
                field: "ContactForm.IncomingMessages",
                width: 90,
                headerName: t("admin.columns.contactMessagesIn"),
                valueGetter: (value, row) => row?.ContactForm?.IncomingMessages,
              },
              {
                field: "ContactForm.OutgoingMessages",
                width: 90,
                headerName: t("admin.columns.contactMessagesOut"),
                valueGetter: (value, row) => row?.ContactForm?.OutgoingMessages,
              },

              {
                field: "Emails.Count",
                width: 120,
                headerName: t("admin.columns.count"),
                valueGetter: (value, row) => row?.Emails?.Count,
              },
              {
                field: "Emails.LastSync",
                width: 100,
                headerName: t("admin.columns.sync"),
                description: t("admin.columns.emailLastSync"),
                valueGetter: (value, row) => row?.Emails?.LastSync,
                valueFormatter: (v) => {
                  if (v) {
                    return moment(v).format("DD.MM.yyyy");
                  }
                  return "";
                },
              },

              {
                field: "Nota.Sent",
                width: 80,
                headerName: t("admin.columns.sent"),
                valueGetter: (value, row) => row?.Nota?.Sent,
              },

              {
                field: "Nota.Received",
                width: 80,
                headerName: t("admin.columns.received"),
                valueGetter: (value, row) => row?.Nota?.Received,
              },

              {
                field: "CourtPortals.LawsuitsCount",
                width: 80,
                headerName: t("admin.columns.lawsuits"),
                valueGetter: (value, row) => row?.CourtPortals?.LawsuitsCount,
              },
              {
                field: "CourtPortals.PortalsCount",
                width: 80,
                headerName: t("admin.columns.portals"),
                valueGetter: (value, row) => row?.CourtPortals?.PortalsCount,
              },
              {
                field: "CourtPortals.DocumentsCount",
                width: 80,
                headerName: t("admin.columns.documents"),
                valueGetter: (value, row) => row?.CourtPortals?.DocumentsCount,
              },
              {
                field: "CourtPortals.LastSync",
                width: 100,
                headerName: t("admin.columns.sync"),
                valueGetter: (value, row) => row?.CourtPortals?.LastSync,
                valueFormatter: (v) => {
                  if (v) {
                    return moment(v).format("DD.MM.yyyy");
                  }
                  return "";
                },
              },

              {
                field: "Dossier.Count",
                width: 100,
                headerName: t("admin.columns.count"),
                valueGetter: (value, row) => row?.Dossier?.Count,
              },
            ]}
            columnGroupingModel={columnGroupingModel}
            hideFooter={true}
            getRowId={(row) => row.Id}
            rows={users}
          />
        )}
      </div>
    </div>
  );
};

export default UserRegister;
